import { Component, ContentChild, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroupDirective, NgForm } from '@angular/forms';
import { AnimatedBootstrapButtonState, IAnimatedBootstrapButtonSettings } from '@cucurb-it/ngx-animated-bootstrap-button';
import { asyncScheduler, BehaviorSubject, of, ReplaySubject } from 'rxjs';
import { debounceTime, delay, filter, first, map, subscribeOn, takeUntil, tap } from 'rxjs/operators';
import { ModalFormHostService } from '../../services/modal-form-host-service/modal-form-host.service';
import { ClearError } from '../manager/actions/clear-error';
import { SetButtons } from '../manager/actions/set-buttons';
import { SetError } from '../manager/actions/set-error';
import { SetHasFooter } from '../manager/actions/set-has-footer';
import { SetIcon } from '../manager/actions/set-icon';
import { SetIsDisabled } from '../manager/actions/set-is-disabled';
import { SetStatus } from '../manager/actions/set-status';
import { SetTitle } from '../manager/actions/set-title';
import { ModalButtons } from '../manager/contracts/modal-buttons';
import { ModalFormStatus } from '../manager/contracts/modal-form-status';
import { ModalHandle } from '../manager/contracts/modal-handle';
import { IState } from '../manager/contracts/state';
import { ModalFormHostManager } from '../manager/modal-form-host-manager';

@Component({
    selector: 'cit-modal-form-host',
    template: `<div>
    <div class="modal-content">
        <div class="modal-header modal-header-blue">
            <h4 class="modal-title">
                <i [class]="icon"></i><span class="ml-3" [innerHtml]="state.title"></span>
            </h4>
        </div>
        <div class="modal-error animated fadeIn" *ngIf="state.status == 'ERROR'">
            <div class="alert alert-danger" role="alert">
                <span [innerHtml]="('ERROR.' + state.error?.code) | translate"></span>
                <span [innerHtml]="('ERROR.' + state.error?.message) | translate"></span>
            </div>
        </div>
        <div class="modal-body" [ngClass]="(!state.hasFooter && state.buttons != modalButtons.None) ? '' : 'body-pb-0'">
            <ng-content></ng-content>
        </div>
        <div *ngIf="state.hasFooter && state.buttons != modalButtons.None" class="modal-footer modal-footer-gray centered-content">
            <button class="btn btn-secondary mx-2" type="button" [hidden]="!(state.buttons % (2 * modalButtons.Cancel) >= modalButtons.Cancel)"  (click)="onCancel()">
                {{ 'SHARED.COMPONENTS.BUTTON.CANCEL' | translate }}
            </button>
            <button class="btn btn-secondary mx-2" type="button" [hidden]="!(state.buttons % (2 * modalButtons.No) >= modalButtons.No)"  (click)="onCancel()">
                {{ 'SHARED.COMPONENTS.BUTTON.NO' | translate }}
            </button>
            <button class="btn btn-info mx-2" type="button" [hidden]="!(state.buttons % (2 * modalButtons.Import) >= modalButtons.Import)" (click)="onCancel()">
                {{ 'SHARED.COMPONENTS.BUTTON.IMPORT' | translate }}
            </button>
            <cit-animated-bootstrap-button [state]="saveButtonState" (stateChanged)="onAnimatedButtonChanged($event)" (clicked)="onSubmit($event)" [options]="modalOptions" [hidden]="!(state.buttons % (2 * modalButtons.Ok) >= modalButtons.Ok)" [disabled]="state.isDisabled">
                {{ 'SHARED.COMPONENTS.BUTTON.OK' | translate }}
            </cit-animated-bootstrap-button>
            <cit-animated-bootstrap-button [state]="saveButtonState" (stateChanged)="onAnimatedButtonChanged($event)" (clicked)="onSubmit($event)" [options]="modalOptions" [hidden]="!(state.buttons % (2 * modalButtons.Save) >= modalButtons.Save)" [disabled]="state.isDisabled">
                {{ 'SHARED.COMPONENTS.BUTTON.SAVE' | translate }}
            </cit-animated-bootstrap-button>
            <cit-animated-bootstrap-button [state]="saveButtonState" (stateChanged)="onAnimatedButtonChanged($event)" (clicked)="onSubmit($event)" [options]="modalOptions" [hidden]="!(state.buttons % (2 * modalButtons.Yes) >= modalButtons.Yes)" [disabled]="state.isDisabled">
                {{ 'SHARED.COMPONENTS.BUTTON.YES' | translate }}
            </cit-animated-bootstrap-button>
            <cit-animated-bootstrap-button [state]="saveButtonState" (stateChanged)="onAnimatedButtonChanged($event)" (clicked)="onSubmit($event)" [options]="modalOptions" [hidden]="!(state.buttons % (2 * modalButtons.Add) >= modalButtons.Add)" [disabled]="state.isDisabled">
                {{ 'SHARED.COMPONENTS.BUTTON.ADD' | translate }}
            </cit-animated-bootstrap-button>
        </div>
    </div>
</div>
`,
    styles: [``]
})
export class ModalFormHostComponent implements OnInit, OnDestroy {
    constructor(
        private service: ModalFormHostService
    ) { }

    private destroyed$ = new ReplaySubject<boolean>();
    private manager = new ModalFormHostManager();

    public modalButtons = ModalButtons;
    saveButtonState = AnimatedBootstrapButtonState.Default;
    modalOptions: Partial<IAnimatedBootstrapButtonSettings> = {
        enabledOnSuccess: false,
        submittingTimeOut: 500
    };

    @Input()
    get handle(): ModalHandle { return this.handle$.value; }
    set handle(value: ModalHandle) { this.handle$.next(value); }
    handle$: BehaviorSubject<ModalHandle> = new BehaviorSubject<ModalHandle>(undefined);

    @Input()
    set title(value: string) { this.manager.dispatch(new SetTitle(value)); }

    @Input()
    set icon(value: string) { this.manager.dispatch(new SetIcon(value)); }

    @Input()
    set buttons(value: number) { this.manager.dispatch(new SetButtons(value)); }

    @Input()
    set hasFooter(value: boolean) { this.manager.dispatch(new SetHasFooter(value)); }

    @Input()
    set isDisabled(value: boolean) { this.manager.dispatch(new SetIsDisabled(value)); }

    @ContentChild(FormGroupDirective)
    private get formGroup(): FormGroupDirective { return this.formGroup$.value; }
    private set formGroup(value: FormGroupDirective) { this.formGroup$.next(value); }
    private formGroup$ = new BehaviorSubject<FormGroupDirective>(undefined);

    get form(): NgForm { return this.form$.value; }
    set form(value: NgForm) { this.form$.next(value); }
    form$ = new BehaviorSubject<NgForm>(null);

    state: Partial<IState> = {};

    ngOnInit() {
        this.handle = this.service.current;
        this.manager.state$
            .pipe(
                takeUntil(this.destroyed$),
                subscribeOn(asyncScheduler)
            )
            .subscribe(state => this.state = state);

        this.manager.state$
            .pipe(
                takeUntil(this.destroyed$),
                subscribeOn(asyncScheduler),
                filter(state => !!state),
                map(state => state.status),
                tap(status => {
                    if (status === ModalFormStatus.Iddle) {
                        this.saveButtonState = AnimatedBootstrapButtonState.Default;
                    } else if (status === ModalFormStatus.Pending) {
                        this.saveButtonState = AnimatedBootstrapButtonState.Submitting;
                    } else if (status === ModalFormStatus.Error) {
                        this.saveButtonState = AnimatedBootstrapButtonState.Error;
                    } else if (status === ModalFormStatus.Finished) {
                        this.saveButtonState = AnimatedBootstrapButtonState.Success;
                    }
                })
            )
            .subscribe();

        this.service.current$
            .pipe(
                takeUntil(this.destroyed$),
                subscribeOn(asyncScheduler),
                filter(handle => !!handle),
                filter(handle => handle.modal === this.handle.modal),
                debounceTime(20)
            )
            .subscribe(handle => {
                this.form = handle.form;

                if (handle.status === ModalFormStatus.Finished) {
                    this.manager.dispatch(new SetStatus(ModalFormStatus.Finished), new SetIsDisabled(true));
                    of(true)
                        .pipe(
                            first(),
                            tap(() => this.saveButtonState = AnimatedBootstrapButtonState.Success),
                            delay(1250),
                            tap(() => this.handle.modal.hide())
                        )
                        .subscribe();
                } else if (handle.status === ModalFormStatus.Error) {
                    of(true)
                        .pipe(
                            first(),
                            tap(() => this.saveButtonState = AnimatedBootstrapButtonState.Error),
                            delay(600),
                            tap(() => this.manager.dispatch(new SetError(handle.error)))
                        )
                        .subscribe();
                }
            });
    }

    ngOnDestroy(): void {
        this.service.destroyModalRef();
        this.handle = null;

        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    onAnimatedButtonChanged(state) {
    }

    onSubmit(event: Event) {
        this.manager.dispatch(new ClearError(), new SetStatus(ModalFormStatus.Pending));

        let submitting = false;
        if (!!this.form) {
            for (const key in this.form.form.controls) {
                if (this.form.controls.hasOwnProperty(key)) {
                    if (this.form.form.controls[key].invalid) {
                        this.form.form.controls[key].markAsTouched();
                    }
                }
            }

            if (this.form.valid) {
                this.saveButtonState = AnimatedBootstrapButtonState.Submitting;
                this.form.onSubmit(null);
                submitting = true;
            }
        }

        if (!!this.formGroup) {
            this.formGroup.ngSubmit.emit(this.formGroup.value);
            submitting = true;
        }

        if (!submitting) {
            this.manager.dispatch(new SetStatus(ModalFormStatus.Iddle));
        }
    }

    onCancel() {
        this.handle.modal.hide();
    }
}
